import { Injectable } from '@angular/core';
import { CustomerdatabaseService } from './customerdatabase.service';
import { JobOfferService } from './jobOfferService.service';
import { v4 as uuidv4 } from 'uuid';
import { FirebaseConstants } from '../shared/FirebaseConstants';
import { filter, take, withLatestFrom } from 'rxjs/operators';


interface Location {
    calendlyLink: string;
    email: string;
    hausnummer: string;
    name: string;
    ort: string;
    plz: string;
    standortEdit: boolean;
    strasse: string;
    telefon: string;
    webseite: string;
    id: string;
}

@Injectable({
  providedIn: 'root'
})
//this service is responsible for migrating job offers from old format to new format
//first it will automatically check
export class MigratorService {
    private _originalLocation = [];

    private _jobOffersHasBeenFetchedOnce = false;
    private _deletedJobOffersHasBeenFetchedOnce = false;
    constructor(
        private customerdatabaseService: CustomerdatabaseService,
        private jobOfferService: JobOfferService
    ) { 
        this.customerdatabaseService.isAuthenticatedOnCustDb$.subscribe((isAuth) => {
            if (isAuth) {
                this.loadOriginalLocations();
                this.upgradeDeletedJobOfferFlowcharts();
            }
        });

        this.jobOfferService.isJobOfferDataFetched$
        .pipe(filter(v => v === true && this._originalLocation.length > 0), take(1))
        .subscribe((isFetched) => {
            if (isFetched) {
                //after job offers are fetched, check if any location needs to be updated.
                const allJobOffers = this.jobOfferService.allJobOffers
                .map((doc) => ({ ...doc, collectionName: FirebaseConstants.jobOffers }));
                this.updateIdOfLocationsInJobOffers(allJobOffers);
                this._jobOffersHasBeenFetchedOnce = true;
            }
        });

        this.jobOfferService.isJobOfferDataDeleted$
        .pipe(filter(v => v === true && this._originalLocation.length > 0), take(1))
        .subscribe((isFetched) => {
            if (isFetched) {
                const deletedJobOffers = this.jobOfferService.deletedJobOffers
                .map((doc) => ({ ...doc, collectionName: FirebaseConstants.deletedJobOffers }));
                //after job offers are fetched, check if any location needs to be updated.
                this.updateIdOfLocationsInJobOffers(deletedJobOffers);
                this._deletedJobOffersHasBeenFetchedOnce = true;
            }
        });

        this.customerdatabaseService.locations$.pipe(
            withLatestFrom(
                this.customerdatabaseService.isAuthenticatedOnCustDb$
            ),
            filter(([locations, isAuth]) => isAuth === true && locations.length > 0)
        ).subscribe(async ([locations, isAuth]) => {
            if (isAuth) {
                //for migrating locations from profile to job offers
            await this.upgradeJobOffersLocations(locations);
            console.log('MIGRATOR SERVICE: Upgrading job offers locations if needed');
            console.log(locations);
            this._originalLocation = locations;
            }
        });

    }

    //todo
    //use batching please. takes too long.

    private async updateIdOfLocationsIfNeeded() {
        let needsUpdate = false;
        this._originalLocation.forEach((location) => {
            if (!location.id) {
                let newId = this.generateId();
                //then check duplicate (rare but possible)
                while (this._originalLocation.find((loc) => loc.id === newId)) {
                    newId = this.generateId();
                }
                location.id = newId;
                needsUpdate = true;
            }
        })

        if (needsUpdate) {
            //then updated profile here
            this.customerdatabaseService.updateProfile({
                standorte: this._originalLocation
            })
        }
    }

    //for updating old jobOffers without ids in the locations
    //so that it matches the profile locations
    private async updateIdOfLocationsInJobOffers(jobs) {

        jobs.forEach(async (jobOffer) => {
         //old version
            const flowcharts = jobOffer.flowcharts;
            let locations = flowcharts?.map((fc) => fc.location) ?? [];
            
            //check everything, especially the name and ort.
            locations.forEach((loc) => {
                this._originalLocation.forEach(async (changedLoc) => {
                    const updatedFlowcharts = flowcharts.map((fc) => {
                        if (fc.location.id === changedLoc.id) {
                            return {
                                ...fc,
                                location: changedLoc
                            };
                        }
                        return fc;
                    });

                    //there is no id (very old version), match by name and ort
                    if (!loc.id && loc.name === changedLoc.name && loc.ort === changedLoc.ort) {
                        await this.customerdatabaseService.app2.firestore
                            .collection(jobOffer.collectionName)
                            .doc(jobOffer.id)
                            .update({ flowcharts: updatedFlowcharts });
                    }
                });
            });

            //new version
            const flowchartsV2 = await this.customerdatabaseService.app2.firestore
            .collection(jobOffer.collectionName)
            .doc(jobOffer.id)
            .collection('flowcharts').get();
            let locationsV2 = flowchartsV2?.docs?.map((doc) => ({docId: doc.id, ...doc.data().location})) ?? [];

            //check everything, especially the name and ort.
            locationsV2.forEach((loc) => {
                this._originalLocation.forEach(async (changedLoc) => {
                    //there is no id (very old version), match by name and ort
                    if (!loc.id && loc.name === changedLoc.name && loc.ort === changedLoc.ort) {
                        await this.customerdatabaseService.app2.firestore
                            .collection(jobOffer.collectionName)
                            .doc(jobOffer.id)
                            .collection('flowcharts')
                            .doc(loc.docId)
                            .update({ location: changedLoc });
                    }
                });
            });

        })
    
    }

    private async loadOriginalLocations() {
        console.log('MIGRATOR SERVICE: Loading original locations from profile');
        const profileColl = this.customerdatabaseService.getProfile()
        if (!profileColl) return;
        const profileDocs = await profileColl.get();
        if (!profileDocs.empty) {
            const profileData = profileDocs.docs[0].data();
            this._originalLocation = profileData.standorte || [];
        }

        //now check if they have ids, if not, generate and update the profile.
        this.updateIdOfLocationsIfNeeded();
    }

    private generateId(): string {
        return uuidv4();
    }

    private findChangedLocation(newLocations) {
        let changedLocations = [];
        newLocations.forEach((newLoc) => {
            const newLocId = newLoc.id;
            const originalLoc = this._originalLocation.find((origLoc) => origLoc.id === newLocId);
            if (!originalLoc) return; //means a new location was added.

            //compare all fields
            for (const key in newLoc) {
                if (newLoc[key] !== originalLoc[key]) {
                    changedLocations.push(newLoc);
                    break; //no need to check further fields
                }
            }
        });
        return changedLocations;
    }

    async upgradeChangedLocations(sortedByUpdatedJobOffers, changedLocation) {
        for (const jobOffer of sortedByUpdatedJobOffers) {

            //old version
            const flowcharts = jobOffer.flowcharts;
            let locations = flowcharts?.map((fc) => fc.location) ?? [];
            
            
            //check everything, especially the name and ort.
            locations.forEach((loc) => {
                changedLocation.forEach(async (changedLoc) => {
                    const updatedFlowcharts = flowcharts.map((fc) => {
                        if (fc.location.id === changedLoc.id) {
                            return {
                                ...fc,
                                location: changedLoc
                            };
                        }
                        return fc;
                    });

                    if (loc.id === changedLoc.id) {
                        // If the location has changed, update it in the new format
                        await this.customerdatabaseService.app2.firestore
                            .collection(jobOffer.collectionName)
                            .doc(jobOffer.id)
                            .update({ flowcharts: updatedFlowcharts });
                    }

                });
            });


            //new version
            const flowchartsV2 = await this.customerdatabaseService.app2.firestore
            .collection(jobOffer.collectionName)
            .doc(jobOffer.id)
            .collection('flowcharts').get();
            let locationsV2 = flowchartsV2?.docs?.map((doc) => ({docId: doc.id, ...doc.data().location})) ?? [];

            //check everything, especially the name and ort.
            locationsV2.forEach((loc) => {
                changedLocation.forEach(async (changedLoc) => {
                    if (loc.id === changedLoc.id) {
                        // If the location has changed, update it in the new format
                        await this.customerdatabaseService.app2.firestore
                            .collection(jobOffer.collectionName)
                            .doc(jobOffer.id)
                            .collection('flowcharts')
                            .doc(loc.docId)
                            .update({ location: changedLoc });
                    }
                });
            });
        }
    }

    async upgradeDeletionLocations(sortedByUpdatedJobOffers, deletedLocations) {

        //handle deleted locations if needed
        if (deletedLocations.length > 0) {
            // Perform actions for deleted locations
            console.log('MIGRATOR SERVICE: Some locations were deleted. Please check job offers for references to deleted locations.');

            sortedByUpdatedJobOffers.forEach(async (jobOffer) => {
        
            //old version
            const flowcharts = jobOffer.flowcharts;
            let locations = flowcharts?.map((fc) => fc.location) ?? [];
            
            
            //check everything, especially the name and ort.
            locations.forEach((loc) => {
                deletedLocations.forEach(async (deletedLoc) => {
                    const updatedFlowcharts = flowcharts.filter((fc) => {
                        if (fc.location.id) {
                            return fc.location.id !== deletedLoc.id;
                        }
                        else {
                            return fc.location.name !== deletedLoc.name || fc.location.ort !== deletedLoc.ort;
                        }
                    });

                    if (loc.id === deletedLoc.id) {
                        // If the location has changed, update it in the new format
                        await this.customerdatabaseService.app2.firestore
                            .collection(jobOffer.collectionName)
                            .doc(jobOffer.id)
                            .update({ flowcharts: updatedFlowcharts });
                    }

                });
            });


            //new version
            const flowchartsV2 = await this.customerdatabaseService.app2.firestore
            .collection(jobOffer.collectionName)
            .doc(jobOffer.id)
            .collection('flowcharts').get();
            let locationsV2 = flowchartsV2?.docs?.map((doc) => ({docId: doc.id, ...doc.data().location})) ?? [];

            //check everything, especially the name and ort.
            locationsV2.forEach((loc) => {
                deletedLocations.forEach(async (deletedLoc) => {
                    if (loc.id === deletedLoc.id) {
                        // If the location has changed, update it in the new format
                        await this.customerdatabaseService.app2.firestore
                            .collection(jobOffer.collectionName)
                            .doc(jobOffer.id)
                            .collection('flowcharts')
                            .doc(loc.docId).delete();
                    }
                });
            });
        })
        }
    }

    async getCombinedJobOffers() {
        const combinedJobOffers = [];
        if (this._jobOffersHasBeenFetchedOnce) {
            combinedJobOffers.push(...this.jobOfferService.allJobOffers
                .map((doc) => ({ ...doc, collectionName: FirebaseConstants.jobOffers })));
        } else {
            combinedJobOffers.push(...(await this.jobOfferService.fetchAllJobOffers()).map((doc) => ({ ...doc, collectionName: FirebaseConstants.jobOffers })));
        }
        if (this._deletedJobOffersHasBeenFetchedOnce) {
            combinedJobOffers.push(...this.jobOfferService.deletedJobOffers
                .map((doc) => ({ ...doc, collectionName: FirebaseConstants.deletedJobOffers })));
        } else {
            const deletedJobOffers = await this.jobOfferService.fetchDeletedJobOffers();
            combinedJobOffers.push(...deletedJobOffers.map((doc) => ({ ...doc, collectionName: FirebaseConstants.deletedJobOffers })));
        }
        return combinedJobOffers;
    }

    async upgradeJobOffersLocations(newLocations) {
        const combinedJobOffers = await this.getCombinedJobOffers();

        const sortedByUpdatedJobOffers = combinedJobOffers.sort((a, b) => {
            const dateA = a.updated ? new Date(a.updated).getTime() : 0;
            const dateB = b.updated ? new Date(b.updated).getTime() : 0;
            return dateB - dateA;
        });


        //for changedLocations
        const changedLocation = this.findChangedLocation(newLocations);
        console.log('Changed Locations:', changedLocation);
        await this.upgradeChangedLocations(sortedByUpdatedJobOffers, changedLocation);


        //for deleted locations
        const deletedLocations = this._originalLocation
        .filter(origLoc => !newLocations.find(newLoc => newLoc.id === origLoc.id));
        await this.upgradeDeletionLocations(sortedByUpdatedJobOffers, deletedLocations);
        
    }

    //for job offer before 12.01.2026
    //some deleted job offers have a flowchart inside the job offers collection
    //(forgot to update to the subcollection inside deletedJobOffers)
    //hence the code below.
    async upgradeDeletedJobOfferFlowcharts() {
        const deletedJobOffers = await this.customerdatabaseService.getDeletedJobOffers().get();
        deletedJobOffers.forEach(async (jobOffer) => {
            const flowcharts = await this
            .customerdatabaseService
            .getFlowchartsFromSubCollection(jobOffer.id, FirebaseConstants.jobOffers);
            if (!flowcharts) return;

            const movePromises = [];
            const deletePromises = [];
            flowcharts.forEach((flow) => {
                const docId = flow.docId;
                delete flow.docId;
                //move each flowchart to the deletedJobOffers subcollection
                this.customerdatabaseService.app2.firestore
                    .collection(FirebaseConstants.deletedJobOffers)
                    .doc(jobOffer.id)
                    .collection(FirebaseConstants.flowcharts)
                    .doc(docId)
                    .set(flow);
                    
                //delete from the old location
                this.customerdatabaseService.app2.firestore
                    .collection(FirebaseConstants.jobOffers)
                    .doc(jobOffer.id)
                    .collection(FirebaseConstants.flowcharts)
                    .doc(docId)
                    .delete();
            });

            // await Promise.all([movePromises, deletePromises]);
        });

    }
}